import React, { useId } from "react";
import { ModalBody } from "../../../design-system/components/composite/Modal/Modal";
import {
  Footer,
  FooterNote,
  Header,
  HeaderCopy,
  PrimaryAction,
  SecondaryAction,
  Shell,
  Subtitle,
  Title,
} from "./DataStoryModalShell.styles";
import type { DataStoryModalShellProps } from "./DataStoryModalShell.types";

/** Gutter (the shell itself pads 28) shared by the recipe picker and the details step bodies. */
const DEFAULT_BODY_PADDING = "0";
/** Hardik: pinned near the top like design F04 (110px), not centred. GAP: no spacing token for 110px. */
const DIALOG_TOP = "6.875rem";

export const DataStoryModalShell: React.FC<DataStoryModalShellProps> = ({
  open,
  title,
  subtitle,
  onClose,
  primaryActionLabel,
  onPrimaryAction,
  primaryActionDisabled = false,
  isSubmitting = false,
  bodyPadding = DEFAULT_BODY_PADDING,
  bodyOverflowY = "auto",
  footerNote,
  secondaryActionLabel,
  onSecondaryAction,
  notice,
  children,
  className,
}) => {
  // The dialog needs a stable id to point aria-labelledby at its own visible
  // title; the shell is rendered once per step, so a generated id keeps two
  // simultaneously-mounted shells from colliding.
  const titleId = `data-story-modal-title-${useId()}`;

  return (
    <Shell
      // A body that must not clip its popover sits inside a dialog that must
      // not clip it either — one signal drives both.
      $allowOverflow={bodyOverflowY === "visible"}
      open={open}
      onClose={onClose}
      size="lg"
      pinnedTop={DIALOG_TOP}
      ariaLabelledBy={titleId}
      className={className}
    >
      <Header onClose={onClose}>
        <HeaderCopy>
          <Title id={titleId}>{title}</Title>
          {subtitle ? <Subtitle>{subtitle}</Subtitle> : null}
        </HeaderCopy>
      </Header>
      {notice}
      <ModalBody padding={bodyPadding} overflowY={bodyOverflowY}>
        {children}
      </ModalBody>
      <Footer padding="0">
        {footerNote ? <FooterNote>{footerNote}</FooterNote> : null}
        {secondaryActionLabel ? (
          <SecondaryAction type="button" onClick={onSecondaryAction}>
            {secondaryActionLabel}
          </SecondaryAction>
        ) : null}
        <PrimaryAction
          variant="primary-dark"
          size="md"
          loading={isSubmitting}
          disabled={primaryActionDisabled || isSubmitting}
          onClick={onPrimaryAction}
        >
          {primaryActionLabel}
        </PrimaryAction>
      </Footer>
    </Shell>
  );
};

export default DataStoryModalShell;
